/**
 * @fileoverview The one header every public page wears: the landing page, a
 * gardener's garden page, a plant's or bed's page, a share link's page and
 * Gardens on Grow (owner, 2026-10-15: "the pages - landing, public garden
 * pages all need a unified header"; "the public gardens pages still use the
 * grow without the by garden gameplan").
 *
 * Left: Grow's sprout, "Grow" and a smaller, muted "by Garden Gameplan",
 * beside it from sm up and under it on a phone, linking home to the landing
 * page (router state { home: true }, so the landing shows even after "Start
 * your garden" was tapped in this visit; showsLanding). Right: "Gardens" (the
 * listing), then for a visitor "Sign in" and the green "Start your garden"
 * ("Start free" on a phone, where the full words crowd the row), and for a
 * signed-in gardener "Back to my garden" (Today) in its place.
 *
 * The landing page hands in its own sign-in and start (they open its sign-in
 * and sign-up screens in place); elsewhere "Sign in" goes to Today, which
 * shows the sign-in page to a visitor, and "Start your garden" to /?start=1,
 * which opens sign-up (arrivalSource).
 *
 * Where: LandingPage, and PublicBar (gardenPageParts) on every public page.
 * Words (en/es): landing.brand, landing.brandBy, landing.gardensLink,
 * landing.signIn, publicBar.label, publicBar.start, publicBar.startShort,
 * publicBar.back.
 *
 * @module PublicHeader
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Sprout } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useAuth } from '@/shared/hooks/useAuth';

/** What the header is given: the landing page's own ways in, if any. */
interface PublicHeaderProps {
  /** Opens sign-in in place (the landing page); without it "Sign in" goes to Today. */
  onSignIn?: () => void;
  /** Opens sign-up in place (the landing page); without it "Start your garden" goes to /?start=1. */
  onStart?: () => void;
}

/** A text link's look in the header. */
const TEXT_LINK = 'whitespace-nowrap text-sm font-semibold text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm';

/** The green pill's look: "Start your garden" or "Back to my garden". */
const PILL = 'flex h-9 shrink-0 items-center whitespace-nowrap rounded-full bg-primary px-3.5 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * The public pages' header.
 *
 * @param props - PublicHeaderProps.
 */
export const PublicHeader: React.FC<PublicHeaderProps> = ({ onSignIn, onStart }) => {
  const { reactTranslate: t } = useTranslation();
  const { currentUser } = useAuth();
  /** "Start your garden", shortened on a phone. */
  const startWords = (
    <>
      <span className="sm:hidden">{t('publicBar.startShort')}</span>
      <span className="hidden sm:inline">{t('publicBar.start')}</span>
    </>
  );
  return (
    <header className="border-b bg-background">
      <nav aria-label={t('publicBar.label')} className="mx-auto flex h-14 w-full max-w-5xl items-center justify-between gap-3 px-4 md:px-8">
        <Link to="/" state={{ home: true }} className="flex min-w-0 items-center gap-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <Sprout className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
          {/* Beside "Grow" from sm up; under it on a phone, where beside it crowds the links. */}
          <span className="flex min-w-0 flex-col leading-tight sm:flex-row sm:items-baseline sm:gap-1.5">
            <span className="font-grow text-xl font-bold text-primary">{t('landing.brand')}</span>
            <span className="whitespace-nowrap font-grow text-[11px] font-semibold text-muted-foreground sm:text-sm">{t('landing.brandBy')}</span>
          </span>
        </Link>
        <div className="flex shrink-0 items-center gap-2.5 sm:gap-4">
          <Link to="/gardens" className={TEXT_LINK}>{t('landing.gardensLink')}</Link>
          {currentUser ? (
            <Link to="/today" className={PILL}>{t('publicBar.back')}</Link>
          ) : (
            <>
              {onSignIn
                ? <button type="button" onClick={onSignIn} className={TEXT_LINK}>{t('landing.signIn')}</button>
                : <Link to="/today" className={TEXT_LINK}>{t('landing.signIn')}</Link>}
              {onStart
                ? <button type="button" onClick={onStart} className={PILL}>{startWords}</button>
                : <Link to="/?start=1" className={PILL}>{startWords}</Link>}
            </>
          )}
        </div>
      </nav>
    </header>
  );
};
